import React, { useMemo, useState } from 'react';
import { CTA_TYPE, CTA_SIZE, DATE_TIME_FORMATS } from '../../../data/constants';
import CtaComponent from '../../../components/reusable-ui/cta';
import { Box, Modal } from '@mui/material';
import DateRangePicker from '../../../components/reusable-ui/date-range-picker';
import { useSelector } from 'react-redux';
import moment, { Moment } from 'moment';
import { convertMomentToString } from '../../../helpers/moment-helper';
import CustomDropdown, { CustomDropdownCountType } from '../../../components/reusable-ui/custom-dropdown';
import { convertArrayToObject } from '../../../helpers/catalog-helper';
import { useDispatch } from 'react-redux';
import { fetchCommonData } from '../../../redux/slices';
import { METHODS } from '../../../data/api-method-constants';
import { ENDPOINT } from '../../../services/apiUrls';
import { getActiveBranches } from '../../../helpers/common-helper';
import { XML_REPORT_TYPES_LIST } from '../../../data/accounting-constants';

const DATE_ERROR = "You can select date range max upto 30 days!";
const MAX_DAYS_LIMIT = 30, MAX_DAYS_RANGE = 90;
interface Props {
  modalState: any;
  closeModal: any;
  selectedReportType: string;
  mappedBranches: string[];
}

const defaultDateRange = {
  startDate: moment(moment().subtract(1, 'd')).startOf('day'),
  endDate: moment(moment().subtract(1, 'd')).endOf('day'),
};

const defaultDateStringObj = {
  startDate: convertMomentToString(
    defaultDateRange.startDate,
    DATE_TIME_FORMATS.dayMonthYear
  ),
  endDate: convertMomentToString(
    defaultDateRange.endDate,
    DATE_TIME_FORMATS.dayMonthYear
  ),
};

const GenerateModal = (props: Props) => {
  const { modalState, closeModal, selectedReportType } = props;
  const [state, setState] = useState<any>({
    selectedBranch: '',
    dateRange: defaultDateStringObj,
    isLoading: false,
  });
  const permittedBranchesData = useSelector((state:any)=> state.businessReducer?.userData?.permittedBranches );
  const permittedInventoryBranches = useSelector((state:any)=> state.businessReducer.permittedInventoryBranches || []);
  const isSalesReport = selectedReportType === XML_REPORT_TYPES_LIST[0].value;
  const branchDataList = isSalesReport
    ? permittedBranchesData
    : permittedInventoryBranches;
  const branchUniqueId = isSalesReport ? "branchId" : "mid";
  const filteredBranchesData = useMemo(
    () =>
      branchDataList.filter((val: any) =>
        props.mappedBranches.includes(val[branchUniqueId])
      ),
    [branchDataList, props.mappedBranches, branchUniqueId]
  );
  const filteredBranchesMap = useMemo(
    () => convertArrayToObject(filteredBranchesData, branchUniqueId),
    [filteredBranchesData, branchUniqueId]
  );
  const dispatch = useDispatch();
  const CTA_CONFIG = {
    cancel: {
      ctaSize: CTA_SIZE.medium,
      customClass: 'rounded-[8px] ml-auto',
      ctaType: CTA_TYPE.secondary_cta,
      ctaText: 'Cancel',
      onCtaClick: handleReset,
    },
    generate: {
      ctaSize: CTA_SIZE.medium,
      customClass: 'rounded-[8px] ml-20',
      ctaType: CTA_TYPE.active_cta,
      ctaText: 'Generate XML',
      onCtaClick: generateXML,
      isDisabled:
        state.selectedBranch === '' || state.dateRange.startDate === '',
    },
    okay: {
      ctaSize: CTA_SIZE.medium,
      customClass: 'rounded-[8px] ml-auto',
      ctaType: CTA_TYPE.active_cta,
      ctaText: 'Okay',
      onCtaClick: handleReset,
    },
  };
  const DROPDOWN_CONFIG = {
    branch: {
      parentClass: ``,
      addEmptyOption: false,
      optionsMap: getActiveBranches(filteredBranchesMap),
      onSubmit: (value: any) => {
        setState({ ...state, selectedBranch: value[0] });
      },
      uniqueKey: branchUniqueId,
      isMultiSelect: false,
      dropdownHeading: 'Branches',
      preSelectedValues: state.selectedBranch,
      placeholder: 'Select branch',
      getListItemText: (data: any) => data?.branchName || '',
      showCountType: CustomDropdownCountType.onePlus,
      countSuffix: 'Branches',
    },
  };

  const onDateRangeSubmission = (data: {
    startDate: Moment;
    endDate: Moment;
  }) => {
    let dateRangeValue = {
      startDate: convertMomentToString(
        data.startDate,
        DATE_TIME_FORMATS.dayMonthYear
      ),
      endDate: convertMomentToString(
        data.endDate,
        DATE_TIME_FORMATS.dayMonthYear
      ),
    };
    setState({ ...state, dateRange: dateRangeValue });
  };

  function renderCta(ctaProps: any) {
    return <CtaComponent {...ctaProps} />;
  }

  function handleReset() {
    setState({
      selectedBranch: '',
      dateRange: defaultDateStringObj,
      isLoading: false,
    });
    closeModal();
  }

  function renderDropdown(dropDownConfig: any): JSX.Element {
    return <CustomDropdown {...dropDownConfig}></CustomDropdown>;
  }

  function disabledDate(current: any) {
    return (
      current &&
      (current >= moment().endOf('day') || moment().diff(current, 'days') > MAX_DAYS_RANGE + 1)
    );
  }

  function generateXML() {
    let startTime = moment(
        state?.dateRange?.startDate,
        DATE_TIME_FORMATS.dayMonthYear
      ).format(DATE_TIME_FORMATS.yearMonthDay),
      endTime = moment(
        state?.dateRange?.endDate,
        DATE_TIME_FORMATS.dayMonthYear
      ).format(DATE_TIME_FORMATS.yearMonthDay);
    let payload = {
      periods: [startTime, endTime],
      branchId: state.selectedBranch,
      reportType: selectedReportType,
    };
    dispatch(
      fetchCommonData({
        method: METHODS.POST,
        body: payload,
        url: ENDPOINT.ACCOUNTING.generateXML,
        callback: generateCallback,
      })
    );
    setState({ ...state, isLoading: true });
  }

  function generateCallback(response: any) {
    if (!response.code) {
      window.open(response.url, '_blank');
      setState({...state, isLoading: false})
    }
  }

  function renderLoading() {
    return (
      <>
        <div className="flex justify-between pl-30 pr-20 pt-18 modal-header without-border">
          <h2 className="text-20 font-bold">XML Generator</h2>
        </div>
        <div className="modal-body-content px-30 pb-40 flex ">
          <div className="bg-[#397EF5]/[0.1] pt-1 rounded-[50%] rotate-90">
            <div className="rista-sprite ic-three-blue-dots rounded-full"></div>
          </div>
          <div className="ml-10 black-color/[0.7] text-15">
            Your file is being generated. please bear with us for few moments
          </div>
        </div>
        <div className="modal-footer px-30 py-16 flex ">
          {renderCta(CTA_CONFIG.okay)}
        </div>
      </>
    );
  }

  function renderGenerate() {
    return (
      <>
        <div className="flex justify-between pl-30 pr-20 pt-18 modal-header without-border">
          <h2 className="text-20 font-bold">Generate XML File - {selectedReportType}</h2>
        </div>
        <div className="modal-body-content px-30 pb-40 flex flex-col">
          <div className="text-14 text-[#333]/[.87] mb-8">Date range</div>
          <DateRangePicker
            errorMessage={DATE_ERROR}
            timeDifference={MAX_DAYS_LIMIT}
            popupContainerId="date-popup"
            customClass="w-[100%] h-45 z-[50]"
            disabledDate={disabledDate}
            dateFormat={DATE_TIME_FORMATS.dayMonthYear}
            onSubmit={onDateRangeSubmission}
            defaultDateRange={[
              defaultDateRange.startDate,
              defaultDateRange.endDate,
            ]}
          />
          <div id="date-popup" />
          <div className="text-14 text-[#333]/[.87] mb-8 mt-24">Branches</div>
          {renderDropdown(DROPDOWN_CONFIG.branch)}
        </div>
        <div className="modal-footer px-30 py-16 flex with-border ">
          {renderCta(CTA_CONFIG.cancel)}
          {renderCta(CTA_CONFIG.generate)}
        </div>
      </>
    );
  }
  return (
    <Modal open={modalState.show} onClose={closeModal}>
      <Box
        className={`modal-center min-w-[600px] bg-white sm:h-[50%] overflow-hidden ${
          state.isLoading ? 'min-h-[229px]' : 'min-h-[368px]'
        }`}
      >
        {state.isLoading ? renderLoading() : renderGenerate()}
      </Box>
    </Modal>
  );
};
export default GenerateModal;
